import { useMemo } from "react";
import type { ReactElement, ReactNode } from "react";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Stack, Text, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

interface ILLMSetupSectionHeaderProps {
    title: ReactElement;
    subtitleKey?: string;
    subtitleAutomation?: string;
    tooltip?: string;
    titleChildren?: ReactNode;
    iconName?: IconName;
    gap?: string;
}

export const LLMSetupSectionHeader = ({
    title,
    subtitleKey,
    subtitleAutomation,
    tooltip,
    titleChildren,
    iconName,
    gap = "2px",
}: ILLMSetupSectionHeaderProps) => {
    const titleContent: ReactNode = useMemo(
        () => (
            <Flex gap="4px">
                {iconName ? (
                    <SWReactIcons
                        iconName={iconName}
                        size="sm"
                    />
                ) : null}
                <Flex gap="4px">
                    {title}
                    {tooltip ? (
                        <Tooltip
                            content={
                                <Stack>
                                    <Text
                                        fontVariant="tooltip"
                                        maxWidth="250px"
                                    >
                                        {tooltip}
                                    </Text>
                                </Stack>
                            }
                        >
                            <SWReactIcons
                                iconName="info"
                                iconColor={colorsPalettes.carbon[200]}
                                size="xs"
                            />
                        </Tooltip>
                    ) : null}
                </Flex>
            </Flex>
        ),
        [iconName, title, tooltip],
    );

    return (
        <Stack gap={gap}>
            {titleChildren ? (
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {titleContent}
                    {titleChildren}
                </Flex>
            ) : (
                titleContent
            )}
            {subtitleKey ? (
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.carbon[300]}
                    {...(subtitleAutomation ? { "data-automation": subtitleAutomation } : {})}
                >
                    {translate(subtitleKey)}
                </Text>
            ) : null}
        </Stack>
    );
};
